import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Building2, ClipboardCheck, HardHat, Home as HomeIcon, LineChart, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PropertyCard } from "@/components/PropertyCard";
import { fetchProperties } from "@/lib/api";
import { HOME } from "@/lib/testIds";

const services = [
  { icon: HomeIcon, title: "Residence Management", desc: "Full-service management of purpose-built student accommodation across South Africa." },
  { icon: LineChart, title: "Property Evaluations", desc: "Independent valuations for landlords, funders and investors — accurate and bankable." },
  { icon: ClipboardCheck, title: "Quantity Surveying", desc: "Cost planning, tender advice and financial control on new builds and refurbishments." },
  { icon: HardHat, title: "Architect & Structural", desc: "Design, engineering and technical services delivered by our accredited partners." },
  { icon: Building2, title: "Property Sales", desc: "Student residence buildings and buy-to-let flats — sourced, sold and advised on." },
  { icon: ShieldCheck, title: "NSFAS Support", desc: "Guidance on NSFAS accreditation for landlords and residence owners." },
];

const stats = [
  { k: "25+", v: "Years combined experience" },
  { k: "2017", v: "Managing properties" },
  { k: "100%", v: "B-BBEE contributor" },
  { k: "9", v: "SA cities covered" },
];

export default function HomePage() {
  const { data: featured = [] } = useQuery({
    queryKey: ["properties", "featured"],
    queryFn: () => fetchProperties({ featured: true, limit: 6 }),
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920&auto=format&fit=crop&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-primary-foreground">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline text-secondary"
          >
            University &amp; TVET Residential Management · South Africa · Est. 2017
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="mt-4 text-4xl sm:text-5xl lg:text-7xl font-serif font-semibold leading-[1.05] max-w-4xl"
          >
            Where students <em className="italic text-secondary font-normal">live well</em>, and landlords rest easy.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mt-6 text-base sm:text-lg text-primary-foreground/85 max-w-2xl leading-relaxed"
          >
            Campusco is a woman-led property firm delivering university and TVET
            student residential management, valuations and technical services
            across South Africa. We manage student residential properties
            nationwide — our portfolio spans the country&apos;s major campus cities.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Button asChild className="rounded-sm h-12 px-7 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid={HOME.heroBookBtn}>
              <Link to="/book-appointment">Book Appointment <ArrowRight className="ml-1 w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm h-12 px-7 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary" data-testid={HOME.heroLeasingBtn}>
              <Link to="/leasing">Browse Accommodation</Link>
            </Button>
          </motion.div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-3xl border-t border-primary-foreground/20 pt-8">
            {stats.map((s) => (
              <div key={s.k}>
                <p className="font-serif text-3xl md:text-4xl">{s.k}</p>
                <p className="mt-1 text-xs md:text-sm text-primary-foreground/70">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust marquee */}
      <section className="border-b border-border py-8 overflow-hidden bg-background">
        <div className="marquee">
          <div className="marquee-track overline text-muted-foreground text-sm">
            {["University & TVET Residential Management", "Student Residential Properties Portfolio", "B-BBEE Level 100%", "PPRA Registered", "Woman-Led Enterprise", "Since 2017", "NSFAS Accreditation Support", "25+ Years Combined Experience", "National Footprint"].concat(["University & TVET Residential Management", "Student Residential Properties Portfolio", "B-BBEE Level 100%", "PPRA Registered", "Woman-Led Enterprise", "Since 2017", "NSFAS Accreditation Support", "25+ Years Combined Experience", "National Footprint"]).map((t, i) => (
              <span key={i} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-secondary rounded-full" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-14 items-end">
          <div className="lg:col-span-6">
            <p className="overline text-secondary">What We Do</p>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight">
              A single, credible partner for the full student-housing lifecycle.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-base text-muted-foreground leading-relaxed">
              From residence management and valuations to quantity surveying,
              architecture and structural engineering — Campusco covers the
              services landlords and funders actually need, at rates that stay
              competitive across the country.
            </p>
            <Button asChild variant="ghost" className="mt-6 px-0 h-auto text-primary hover:bg-transparent hover:text-secondary">
              <Link to="/services">Explore all services <ArrowRight className="ml-1 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="bg-background p-8 group hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
                <Icon className="w-8 h-8 text-secondary group-hover:text-secondary" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground group-hover:text-primary-foreground/75 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured properties */}
      {featured.length > 0 && (
      <section className="bg-muted/30 border-y border-border py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="overline text-secondary">Featured Properties</p>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif">
                Handpicked from our national portfolio.
              </h2>
            </div>
            <div className="flex gap-3">
              <Button asChild variant="outline" className="rounded-sm">
                <Link to="/leasing" data-testid="home-view-all-leasing">View all leasing <ArrowUpRight className="ml-1 w-4 h-4" /></Link>
              </Button>
              <Button asChild className="rounded-sm bg-primary text-primary-foreground">
                <Link to="/sales" data-testid="home-view-all-sales">Investment sales <ArrowUpRight className="ml-1 w-4 h-4" /></Link>
              </Button>
            </div>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <PropertyCard key={p.id} property={p} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* About/Leadership teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-6 relative">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1758611972971-1c8b9c6d7822?w=1200&auto=format&fit=crop&q=80"
              alt="Campusco leadership"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-secondary text-secondary-foreground p-6 max-w-[220px] hidden md:block">
            <p className="overline text-[10px]">Woman-Led</p>
            <p className="mt-2 font-serif text-xl leading-tight">Our CEO and CFO are both women.</p>
          </div>
        </div>
        <div className="lg:col-span-6">
          <p className="overline text-secondary">About Campusco</p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif">
            Twenty-five years of property expertise, brought to the sector South Africa needs it in most.
          </h2>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Since 2017, Campusco has quietly built a reputation as one of the
            country&apos;s most flexible and dependable managers of student
            accommodation. We work with landlords, funders and developers to
            get the numbers right, keep buildings full, and keep students safe.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { title: "B-BBEE Level 100%", body: "Fully compliant contributor." },
              { title: "PPRA Registered", body: "Formal industry accreditation." },
              { title: "Nationwide", body: "Jhb, Pta, Cpt, Dbn & beyond." },
              { title: "Flexible Fees", body: "Structures that suit funders." },
            ].map((b) => (
              <div key={b.title} className="border-l-2 border-secondary pl-4">
                <p className="font-serif text-lg">{b.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{b.body}</p>
              </div>
            ))}
          </div>
          <Button asChild variant="ghost" className="mt-8 px-0 h-auto text-primary hover:bg-transparent hover:text-secondary">
            <Link to="/about">Read our story <ArrowRight className="ml-1 w-4 h-4" /></Link>
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1920&auto=format&fit=crop&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="overline text-secondary">Why Wait?</p>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif">
              Book a detailed presentation of our services today.
            </h2>
            <p className="mt-6 text-primary-foreground/80 max-w-2xl">
              Our leasing team is ready to walk you through how we can support
              your building, portfolio or new development.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Button asChild className="rounded-sm h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid={HOME.ctaBookBtn}>
              <Link to="/book-appointment">Book Appointment</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm h-12 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary">
              <Link to="/brochure" target="_blank" rel="noopener">View Brochure</Link>
            </Button>
            <a
              href="tel:+27608362116"
              className="text-center py-3 text-sm text-primary-foreground/80 hover:text-secondary transition-colors"
              data-testid="home-cta-phone"
            >
              or call 060 836 2116
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
